import { useState } from "react";
import { Upload, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

/** Reusable master bulk-import dialog: download template -> pick .xlsx -> upload -> results. */
export function BulkImportButton({ label = "Bulk Upload", testid, templateUrl, importUrl, templateName = "template.xlsx", onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const download = async () => {
    try { const res = await api.get(templateUrl, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = templateName; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("Template download failed"); }
  };
  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(importUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Created ${data.created} · ${data.errors?.length || 0} errors`);
      if (data.created > 0) onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" data-testid={`${testid}-open`} onClick={() => { setOpen(true); setFile(null); setResult(null); }}><Upload className="mr-1.5 h-4 w-4" /> {label}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Button variant="outline" size="sm" data-testid={`${testid}-template`} onClick={download}><Download className="mr-1.5 h-4 w-4" /> Download Template</Button>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 hover:bg-slate-50">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" /><span className="text-sm text-slate-600">{file ? file.name : "Choose .xlsx file"}</span>
              <input type="file" accept=".xlsx,.xls" className="hidden" data-testid={`${testid}-file`} onChange={(e) => { setFile(e.target.files[0]); setResult(null); }} />
            </label>
            <Button data-testid={`${testid}-submit`} onClick={upload} disabled={!file || busy} className="w-full bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}</Button>
            {result && (
              <div className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`${testid}-result`}>
                <div className="flex gap-2"><Badge className="bg-emerald-100 text-emerald-700">Created {result.created}</Badge><Badge className="bg-slate-100 text-slate-600">Total {result.total}</Badge>{result.errors?.length > 0 && <Badge className="bg-red-100 text-red-700">Errors {result.errors.length}</Badge>}</div>
                {result.errors?.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto">
                    {result.errors.map((er, i) => <div key={i} className="border-b border-slate-100 py-1 text-xs text-red-600">Row {er.row}{er.value ? ` (${er.value})` : ""}: {er.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
